import { Controller } from "@hotwired/stimulus"

// Controls modal dialogs using native <dialog> element.
// The dialog is shown via showModal() which places it in the browser's top layer,
// above all other content regardless of z-index.
export default class extends Controller {
  // `static` keeps a binding dialog open on a backdrop click — the Tagesbericht
  // hint must be acknowledged with its own button. Defaults to false, so every
  // existing dialog keeps dismissing on backdrop click.
  static values = { openOnConnect: Boolean, static: Boolean }

  connect() {
    // Handle native close event (triggered by Escape key or close() call)
    this.boundHandleClose = this.handleClose.bind(this)
    this.element.addEventListener("close", this.boundHandleClose)

    // Close when clicking the backdrop (the dialog element itself, not its children)
    this.boundHandleClick = this.handleClick.bind(this)
    if (!this.staticValue) this.element.addEventListener("click", this.boundHandleClick)

    // A <dialog> also closes on Escape, which would dismiss a binding hint without
    // acknowledging it — and it is never rendered again. The dialog's own button
    // stays the way out.
    this.boundHandleCancel = this.handleCancel.bind(this)
    if (this.staticValue) this.element.addEventListener("cancel", this.boundHandleCancel)

    // Open immediately when the server asked for it (e.g. a list row linking to
    // a page with ?open_...=1 so the target dialog shows without a second click).
    if (this.openOnConnectValue) this.open()
  }

  disconnect() {
    // A dialog removed while open (a Turbo Stream replacing its drawer) never fires
    // `close`, so the scroll lock from open() would outlive it.
    if (this.element.open) document.body.style.overflow = ""
    this.element.removeEventListener("close", this.boundHandleClose)
    if (!this.staticValue) this.element.removeEventListener("click", this.boundHandleClick)
    if (this.staticValue) this.element.removeEventListener("cancel", this.boundHandleCancel)
  }

  // Open the dialog as modal
  open() {
    if (this.element.open) {
      this.element.close()
    }
    this.element.showModal()
    // Prevent body scroll when dialog is open
    document.body.style.overflow = "hidden"
  }

  // Close the dialog
  close() {
    this.element.close()
  }

  // Close dialog after a short delay (used for form submissions)
  closeAfterSubmit() {
    setTimeout(() => {
      this.element.close()
    }, 100)
  }

  handleClose() {
    // Restore body scroll when dialog closes
    document.body.style.overflow = ""
  }

  handleCancel(event) {
    event.preventDefault()
  }

  handleClick(event) {
    // Close when clicking the backdrop (the dialog element itself)
    // This works because the dialog's ::backdrop is part of the dialog element
    if (event.target === this.element) {
      this.close()
    }
  }
}
